import { useMemo } from 'react';
import { relateOne } from '../engine/battlefield/relations';

const NOTHING = { cover: null, rangeBand: null, measurement: null, vehicleFacing: null, arc: null, reachGap: null };

/**
 * useLiveMapModifiers - what the geometry says about this shot, right now.
 *
 * The map has always known the range band and the cover; it just made you
 * ask, once per shot, by dragging a tape and clicking Apply. This derives
 * the same two values continuously from the actor→target line.
 *
 * IT LIVES IN THE SHELL, NOT IN THE BATTLEFIELD PANEL. The panel unmounts
 * when the centre column switches to another tab, and modifiers that stop
 * tracking because nobody happens to be looking at the map are worse than
 * modifiers that never tracked at all.
 *
 * IT WRITES NOTHING. The result feeds `projectDefenderFrom`, which is
 * per-attacker by construction - so two clients deriving different cover
 * for the same defender is the normal case rather than a race, and there
 * is no question of which of them is allowed to persist it.
 *
 * The recomputation is keyed on the two token objects, which the sync
 * layer replaces on every move, plus the solids and the weapon. That is
 * precisely "when the geometry changed", with no new bookkeeping: one
 * line-of-sight trace, not a flood.
 */
export default function useLiveMapModifiers(params) {
  const { actorId, targetId, tokens, solids, weaponRange, isGm = false, enabled = true } = params;
  const { targetIsVehicle = false, arcPreset = 'quarters', relationRules } = params;
  const actorToken = actorId ? tokens?.[actorId] : null;
  const targetToken = targetId ? tokens?.[targetId] : null;

  return useMemo(() => {
    // Nothing to say about a combatant who is not on the map, and nothing
    // to say about shooting yourself.
    if (!enabled || !actorToken || !targetToken || actorId === targetId) return NOTHING;

    // The maths lives in `engine/battlefield/relations.ts`, which answers for
    // any number of defenders. This hook wants exactly one - the target you
    // are aiming at - so it asks for one.
    const { cover, rangeBand, measurement, vehicleFacing, arc, reachGap } = relateOne(actorToken, targetToken, solids, {
      weaponRange,
      includeHidden: isGm,
      arcPreset,
      isVehicle: targetIsVehicle,
      rules: relationRules,
    });

    return { cover, rangeBand, measurement, vehicleFacing, arc, reachGap };
  }, [
    enabled,
    actorToken,
    targetToken,
    actorId,
    targetId,
    solids,
    weaponRange,
    isGm,
    targetIsVehicle,
    arcPreset,
    relationRules,
  ]);
}
